<template>
    <div class="-mt-md sm:mt-0" ref="recipecontainer">
        <div class="mb-lg">
            <div class="grid grid-cols-12 gap-md mb-md">
                <div class="col-span-12 lg:col-span-6">
                    <label class="font-semibold mb-xxs inline-block" for="search">Zoeken</label>

                    <div class="relative">
                        <input
                            class="form__input py-[20px] pr-lg px-sm form__input--filled-dark placeholder:text-ellipsis"
                            v-model="searchInput"
                            @input="searchImmediately"
                            id="search"
                            name="search"
                            placeholder="Vul hier een ingrediënt, titel, soort gerecht in"
                            type="text"
                        />

                        <div class="absolute right-sm top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-400 cursor-pointer" @click.prevent="search">
                            <i class="fa-regular fa-search"></i>
                        </div>
                    </div>
                </div>
                <div class="col-span-12 lg:col-span-6 lg:order-first">
                    <label class="font-semibold mb-xxs inline-block" for="search">Gratis recepten</label>

                    <div class="grid grid-cols-2 gap-xs md:gap-0">
                        <button
                            @click="setWithPrep(true)"
                            :class="{ 'bg-primary-400 text-gray-800': with_prep, 'text-primary-400 hover:bg-primary-400 hover:bg-opacity-10': !with_prep }"
                            class="col-span-2 md:col-span-1 cursor-pointer border border-primary-400 transition-colors uppercase text-sm font-semibold py-[20.6px] text-center flex items-center justify-center select-none"
                        >
                            Gratis recepten op de website
                        </button>
                        <button
                            @click="setWithPrep(false)"
                            :class="{ 'bg-primary-400 text-gray-800': !with_prep, 'text-primary-400 hover:bg-primary-400 hover:bg-opacity-10': with_prep }"
                            class="col-span-2 md:col-span-1 cursor-pointer border border-primary-400 transition-colors uppercase text-sm font-semibold py-[20.6px] text-center flex items-center justify-center select-none"
                        >
                            Alle recepten uit alle boeken
                        </button>
                    </div>
                </div>
            </div>

            <div>
                <p @click="toggleFiltersVisible" class="font-semibold mb-xs inline-flex items-center gap-xs xl:pointer-events-none">
                    Meer zoekopties <span :class="{ '-rotate-180': filtersVisible }" class="transition-transform xl:hidden"><i class="fa-regular transition-transform fa-chevron-down"></i></span>
                </p>
                <div class="overflow-hidden transition-all xl:!max-h-[initial] xl:transition-none" :style="`max-height: ${canShowFilter ? calculatedFiltersHeight : 0}px;`">
                    <div class="flex flex-wrap justify-start items-start -m-xs xl:-m-xxs mt-sm" ref="filters">
                        <div class="inline-flex flex-wrap p-xs xl:p-xxs gap-xs w-full md:w-1/3 xl:w-1/5 justify-start items-start" v-for="(cat_group, index) in grouped_cats">
                            <p class="font-semibold w-full text-xs mb-xxs uppercase" v-html="cat_group.title.rendered"></p>
                            <span
                                v-for="(cat, index) in cat_group.acf.filters"
                                :key="index"
                                class="py-xxs px-xs text-xs uppercase hover:text-primary-400 hover:border-primary-400 text-gray-500 border transition-colors inline-flex border-gray-600 text-opacity-80 tracking-[2px] font-semibold cursor-pointer select-none"
                                :class="{
                                    'bg-primary-400 !text-gray-800': active_categories.includes(cat),
                                }"
                                @click.prevent="setCategory(cat)"
                                v-html="cat"
                            >
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="grid gap-y-lg sm:gap-md md:gap-lg lg:gap-xl grid-cols-12">
            <div key="loading" class="flex col-span-12 justify-center" v-if="loading">
                <div class="aspect-[320/520] w-1/4 relative mb-sm sm:mb-md flex justify-center items-center">
                    <i class="fa-regular fa-spinner-third h-[60px] w-auto animate-spin"></i>
                </div>
            </div>
            <template v-else-if="recipes.length">
                <div
                    v-for="(item, index) in recipes"
                    :key="item.id"
                    class="col-span-12"
                    :class="{
                        'xs:col-span-6': index == 0 || index == 1 || index == 6 || index == 7,
                        'xs:col-span-5': index == 2 || index == 5 || index == 8 || index == 11,
                        'xs:col-span-7': index == 3 || index == 4 || index == 9 || index == 10,
                    }"
                >
                    <recipeitem :content="item" />
                </div>
            </template>
            <div class="col-span-12 text-center flex flex-col items-center" v-else>
                <p class="mb-sm">Niet gevonden wat je zocht?</p>
                <button class="btn btn--primary btn--outline" @click="setWithPrep(!with_prep)">Zoek in <template v-if="with_prep"> alle recepten van alle boeken</template><template v-else> gratis recepten op de website</template></button>
            </div>

            <div class="col-span-12">
                <pagination :current-page="currentPage" :total-pages="totalPages" @next-page="filter(currentPage + 1)" @previous-page="filter(currentPage - 1)" @go-to-page="(event) => filter(event.page)"> </pagination>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import recipeitem from './item';
import { debounce } from 'lodash';
import qs from 'qs';
import Vue from 'vue';
var scrollIntoView = require('scroll-into-view');
import translate from 'translate';
translate.key = process.env.MIX_GOOGLE_TRANSLATE_API_KEY;

export default {
    name: 'recipesoverview',

    data() {
        return {
            loading: false,
            active_categories: [],
            recipes: [],
            grouped_cats: [],
            perPage: 12,
            totalPages: 0,
            currentPage: 1,
            searchInput: '',
            with_prep: true,
            filtersVisible: false,
            calculatedFiltersHeight: 0,
        };
    },

    mounted() {
        let urlParams = new URLSearchParams(window.location.search);

        this.with_prep = urlParams.get('w') === null;
        const after = urlParams.getAll('a');
        this.currentPage = parseInt(urlParams.get('f') ?? 1);
        const page = parseInt(urlParams.get('p') ?? 1);
        this.active_categories = urlParams.getAll('c');
        this.searchInput = urlParams.get('s') ?? '';

        this.getCategories();
        this.getRecipes(page, after, false);
    },

    methods: {
        filter(page) {
            this.getRecipes(page);
        },

        setCategory(cat) {
            if (this.active_categories.includes(cat)) {
                this.active_categories = this.active_categories.filter((el) => el !== cat);
            } else {
                this.active_categories.push(cat);
            }

            this.currentPage = 1;

            this.getRecipes(this.currentPage);
        },

        setWithPrep(withPrep) {
            this.with_prep = withPrep;
            this.currentPage = 1;
            this.getRecipes(this.currentPage);
        },

        searchImmediately: debounce(function () {
            this.search();
        }, 500),

        search() {
            this.currentPage = 1;

            this.getRecipes(this.currentPage, null, false);
        },

        async getRecipes(page, after = null, scroll = true) {
            this.loading = true;

            if (scroll) {
                scrollIntoView(this.$refs.recipecontainer);
            }

            const query = await this.buildQueryObject(page, after);
            const queryString = qs.stringify(query, { encode: false });

            axios
                .get(`/api/my-recipes?${queryString}`)
                .then((response) => {
                    if (response.status === 200) {
                        let recipes = response.data.data;
                        if (query.per_page > this.perPage) {
                            const pagesToCut = Math.ceil(response.data.data.length / this.perPage) - 1;
                            recipes = recipes.slice(pagesToCut * this.perPage);
                        }
                        if (page < this.currentPage) {
                            recipes = recipes.reverse();
                        }

                        this.setQueryParams(query, page);

                        this.recipes = recipes;
                        this.totalPages = Math.ceil(response.data.total / this.perPage);
                        this.currentPage = page;
                    } else {
                        this.recipes = [];
                    }
                })
                .finally(() => (this.loading = false));
        },

        async buildQueryObject(page, after) {
            let query = {};
            this.active_categories.forEach((cat, index) => {
                query.q = {
                    and: {
                        and: { ...(query.q?.and?.and ?? { categories: [] }) },
                    },
                };
                query.q.and.and.categories.push(cat);
            });

            if (this.with_prep) {
                query.q = {
                    and: { enrichment_id: 'exists:', ...(query.q?.and ?? {}) },
                };
            }

            if (this.searchInput !== '') {
                let text = this.searchInput;
                if (document.documentElement.lang !== 'nl') {
                    text = await translate(this.searchInput, 'nl');
                }

                query.q = {
                    and: { and: { or: { title: [], description: [] } }, ...(query.q?.and ?? {}) },
                };
                query.q.and.and.or.title.push(`like^10:*${text}*`);
                query.q.and.and.or.description.push(`like:*${text}*`);
                // following lines are too fuzzy (e.g: "tonijn" search returns "komijn")
                //query.q.and.and.or.title.push(`match^10:${text}`);
                //query.q.and.and.or.description.push(`match:${text}`);
            }

            query.per_page = Math.abs(page - this.currentPage) * this.perPage;
            if (query.per_page === 0) {
                query.per_page = this.perPage;
            }

            query.after = [];
            query.s = page >= this.currentPage ? { 'spotlight_ordinal': 'asc', 'open_recipe.source_book.X-publish_date': 'desc', slug: 'asc' } : { 'spotlight_ordinal': 'asc', 'open_recipe.source_book.X-publish_date': 'asc', slug: 'desc' };
            if (this.searchInput !== '') {
                query.s = page >= this.currentPage ? { _score: 'desc', id: 'asc' } : { _score: 'asc', id: 'desc' };
            }

            if (after !== null) {
                query.after = after;
                return query;
            }

            if (page === 1 && this.currentPage === 1) {
                return query;
            }

            if (page > this.currentPage) {
                query.after = this.recipes[this.recipes.length - 1].sort;
                return query;
            }
            query.after = this.recipes[0].sort;
            return query;
        },

        getCategories() {
            const self = this;
            Promise.all([axios.get('/api/recipe-filters'), axios.get('/api/recipe-filter-groups')])
                .then((reponses) => {
                    const filterResponse = reponses[0];
                    const filterGroupResponse = reponses[1];

                    if (filterResponse.status === 200 && filterGroupResponse.status === 200) {
                        this.cats = filterResponse.data.data[0].results;
                        this.grouped_cats = filterGroupResponse.data.data;
                        this.grouped_cats.sort((a, b) => {
                            return b.acf.filters.length - a.acf.filters.length;
                        });
                    } else {
                        this.cats = [];
                    }
                })
                .finally(() => {
                    this.loading = false;
                    self.calculatedFiltersHeight = self.$refs.filters.clientHeight + 16;
                });
        },

        setSearchParams() {},

        setQueryParams(query, page) {
            const searchParams = new URLSearchParams();
            if (page !== 1) {
                searchParams.set('p', page);
            }
            if (page !== 1) {
                query.after.forEach((after) => {
                    searchParams.append('a', after);
                });
            }
            if (this.searchInput && this.searchInput !== '') {
                searchParams.set('s', this.searchInput);
            }
            if (!this.with_prep) {
                searchParams.set('w', this.with_prep);
            }
            this.active_categories.forEach((ac) => {
                searchParams.append('c', ac);
            });

            if (page !== 1) {
                searchParams.set('f', this.currentPage);
            }

            let newRelativePathQuery = window.location.pathname;
            let hasKeys = false;
            for (const key of searchParams.keys()) {
                hasKeys = true;
            }

            if (hasKeys) {
                newRelativePathQuery += '?' + searchParams.toString();
            }
            history.pushState(null, '', newRelativePathQuery);
        },

        toggleFiltersVisible() {
            this.filtersVisible = !this.filtersVisible;
        },
    },

    computed: {
        canShowFilter() {
            return window.innerWidth >= 1400 || this.filtersVisible;
        },
    },

    components: {
        recipeitem,
    },
};
</script>
